/*
 * cart-drawer-a11y.js  —  issue #28 / #27
 *
 * The slide-in <cart-drawer> is a modal, but its focus management is broken:
 * theme.js has trapFocus / lastElement / an Escape handler, yet none of it runs
 * (confirmed in headless AND a real browser — focus never enters the drawer, Tab
 * walks the page behind it, Escape does nothing). theme.js is a minified,
 * source-less bundle we treat as read-only, so this standalone asset layers proper
 * modal behaviour on top of the drawer the theme already opens/closes.
 *
 * It does NOT open or close the drawer or touch the cart — it only reacts to the
 * `.is-open` class the theme toggles, and manages focus + Escape:
 *   - on open : remember the trigger, move focus to the close button
 *   - while open : trap Tab/Shift-Tab inside the drawer
 *   - Escape : route through the theme's OWN close button (keeps state consistent)
 *   - on close : return focus to the trigger — for BOTH mouse and keyboard
 *     (theme.js gated focus-return on keyboard-mode; that shortchanged
 *     screen-reader-with-mouse users, so this is intentionally ungated).
 *
 * The ARIA (role=dialog / aria-modal / aria-label) lives in layout/theme.liquid;
 * it is only meaningful because this script makes the behaviour real.
 */
(function () {
  'use strict';
  if (window.__cartDrawerA11y) return;
  window.__cartDrawerA11y = true;

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), ' +
    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastTrigger = null;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }
  function isOpen(d) {
    return !!d && d.classList.contains('is-open');
  }
  // Excludes display:none (no offsetParent / no rects) and visibility:hidden
  // (the drawer's own closed state, and the empty-state block when populated).
  function isVisible(el) {
    if (el.offsetParent === null && el.getClientRects().length === 0) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  }
  function focusables(d) {
    return Array.prototype.filter.call(d.querySelectorAll(FOCUSABLE), isVisible);
  }

  function onOpen(d) {
    // Remember what to restore focus to (the cart icon, or the Add-to-Cart button
    // when the drawer opens on add). Capture BEFORE moving focus ourselves.
    var active = document.activeElement;
    lastTrigger =
      active && active !== document.body && !d.contains(active)
        ? active
        : document.querySelector('[data-cart-toggle], .cartToggle');

    // Move focus into the dialog once the open has painted.
    var closeBtn = d.querySelector('[data-cart-drawer-close]');
    window.requestAnimationFrame(function () {
      if (!isOpen(d)) return;
      if (d.contains(document.activeElement)) return; // user already tabbed in — don't yank
      var target = closeBtn && isVisible(closeBtn) ? closeBtn : focusables(d)[0] || d;
      // preventScroll: the drawer is position:fixed, so focusing inside it must not
      // scroll the underlying page.
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    });
  }

  function onClose() {
    var t = lastTrigger;
    lastTrigger = null;
    // Fall back to the cart toggle if the original trigger was removed by a cart
    // re-render — otherwise focus would be lost to <body>.
    if (!t || !document.contains(t) || typeof t.focus !== 'function') {
      t = document.querySelector('[data-cart-toggle], .cartToggle');
    }
    // preventScroll: the drawer may have been opened from far down the page; returning
    // focus to the header toggle must not jump the viewport to the top (also neutralises
    // theme.js's own autoFocusLastElement scroll on the keyboard path — see below).
    if (t && typeof t.focus === 'function') t.focus({ preventScroll: true });
  }

  function onKeydown(e) {
    var d = getDrawer();
    if (!isOpen(d)) return; // only act while the cart drawer is the active modal

    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      var closeBtn = d.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.click(); // route through the theme's own close path
      return;
    }

    if (e.key === 'Tab' || e.keyCode === 9) {
      var f = focusables(d);
      // No focusable elements (shouldn't happen — the close button is always there).
      // Do NOT preventDefault: swallowing Tab with nowhere to go would be a keyboard
      // trap (WCAG 2.1.2). Escape (handled above) still closes regardless.
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      var active = document.activeElement;
      if (!d.contains(active)) {
        // focus escaped the dialog — pull it back to the appropriate edge
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function observe(d) {
    if (!d || d.__a11yObserved) return;
    d.__a11yObserved = true;
    var open = isOpen(d);
    if (open) onOpen(d); // in case it is already open at init
    new MutationObserver(function () {
      var now = isOpen(d);
      if (now === open) return;
      open = now;
      if (open) onOpen(d);
      else onClose();
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    observe(getDrawer());
  }

  // Capture phase: intercept Tab/Escape before the page and before theme.js's own
  // (non-functioning) handlers. Guarded to act only while the drawer is open, so it
  // never interferes with normal page navigation or other components' modals.
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
